/**
 * Shows an event agenda in other time zones. Each session is read in its own zone (or the project's), turned into a
 * real instant, and shown in every target zone with a day marker when the date changes. Pure; uses only Intl.
 */
import type { Session } from "@/lib/workflow/types";
import { nowInZone, zonedToUtc } from "./tz";

export interface ZonedTime { day: string; start: string; end?: string; dayShift: number; note?: "gap" | "ambiguous" }

const parse = (day: string, time: string) => {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day), t = /^(\d{1,2}):(\d{2})$/.exec(time);
  return d && t ? [Number(d[1]), Number(d[2]), Number(d[3]), Number(t[1]), Number(t[2])] as const : null;
};
const dayNumber = (iso: string) => { const [y, m, d] = iso.split("-").map(Number); return Date.UTC(y, m - 1, d) / 86400000; };

/** The session's start (and end) in `target`. Null when the date or time cannot be read or a zone is unknown. */
export function convertSession(s: Pick<Session, "day" | "start" | "end" | "timeZone">, projectZone: string, target: string): ZonedTime | null {
  const source = s.timeZone || projectZone;
  const a = parse(s.day, s.start);
  if (!a) return null;
  try {
    const z = zonedToUtc(...a, source);
    const [date, time] = nowInZone(target, z.utc).split("T");
    let end: string | undefined;
    if (s.end) {
      const b = parse(s.day, s.end);
      if (b) end = nowInZone(target, zonedToUtc(...b, source).utc).split("T")[1];
    }
    return { day: date, start: time, ...(end ? { end } : {}), dayShift: dayNumber(date) - dayNumber(s.day), ...(z.kind !== "ok" ? { note: z.kind } : {}) };
  } catch { return null; }
}

export const dayShiftLabel = (n: number) => (n === 0 ? "" : n > 0 ? `+${n} day${n > 1 ? "s" : ""}` : `${n} day${n < -1 ? "s" : ""}`);

/** One line per session: the times in each zone, with a day marker where the date differs from the session's own. */
export function agendaLines(sessions: readonly Session[], projectZone: string, zones: readonly string[]): string[] {
  return sessions.map((s) => {
    const parts = zones.map((z) => {
      const c = convertSession(s, projectZone, z);
      if (!c) return `${z}: ?`;
      const when = c.end ? `${c.start}-${c.end}` : c.start;
      return `${z.split("/").pop()!.replace(/_/g, " ")} ${when}${c.dayShift ? ` (${dayShiftLabel(c.dayShift)})` : ""}`;
    });
    return `${s.day}  ${s.title}: ${parts.join("  |  ")}`;
  });
}

/** CSV rows: the session in its own zone, then start and end in each target zone. */
export function agendaTable(sessions: readonly Session[], projectZone: string, zones: readonly string[]): { header: string[]; rows: string[][] } {
  const header = ["Day", "Start", "End", "Session", "Time zone", ...zones.flatMap((z) => [`${z} day`, `${z} start`, `${z} end`])];
  const rows = sessions.map((s) => [
    s.day, s.start, s.end ?? "", s.title, s.timeZone || projectZone,
    ...zones.flatMap((z) => { const c = convertSession(s, projectZone, z); return c ? [c.day, c.start, c.end ?? ""] : ["", "", ""]; }),
  ]);
  return { header, rows };
}

